import React from 'react';
import { 
  Factory, 
  Droplets, 
  Zap, 
  Activity, 
  Shield, 
  ShieldCheck,
  Gauge,
  Network
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Factory className="w-12 h-12" />,
      title: 'Industrial Automation',
      description: 'As a complete industrial automation solution provider, we deliver end-to-end PLC, SCADA and HMI systems — from programming and panel building to commissioning and equipment supply.',
      features: ['PLC Programming', 'SCADA Programming', 'HMI Programming', 'PLC Panel Building & Commissioning', 'Supply of PLCs', 'Supply of HMI Drives'],
      image: null
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'LPG Remote Valve Control System as per OISD Compliance',
      description: 'We provide LPG Automatic Remote Valve Control Applications as per OISD norms for LPG facilities and industrial environments. We have successfully implemented and commissioned these solutions at multiple industrial locations, delivering compliance-focused engineering with proven, practical deployment experience.',
      image: null
    },
    {
      icon: <ShieldCheck className="w-12 h-12" />,
      title: 'ILSD Solution',
      description: 'We provide ILSD solutions designed according to OISD norms, helping industrial facilities improve safety, monitoring, and reliability. Our offering includes robust PLC networking and SCADA-based report generation — providing automated report generation, historical data logging, trend analysis, and performance reporting for industrial operations.',
      features: ['PLC Networking Solutions', 'Redundant Ring Architecture', 'Industrial Communication Networks', 'Reliable Data Communication Infrastructure', 'SCADA-based Report Generation'],
      image: null
    },
    {
      icon: <Network className="w-12 h-12" />,
      title: 'IIoT Solutions',
      description: 'Cloud-based monitoring dashboards with remote data visualization, data logging and reporting, and real-time industrial data acquisition systems.',
      image: null
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Energy Monitoring System (EMS)',
      description: 'Real-time meter data monitoring with consumption tracking, trend graphs, and daily/hourly reporting for optimal energy management.',
      image: null
    },
    {
      icon: <Droplets className="w-12 h-12" />,
      title: 'Water Monitoring System (WMS)',
      description: 'Advanced flow monitoring with totalizer data, cloud-based SCADA dashboard, and comprehensive reporting & trend visualization.',
      image: null
    },
    {
      icon: <Gauge className="w-12 h-12" />,
      title: 'Pump House & Fire Water Automation',
      description: 'Automated pressure monitoring with priority-based fire engine sequencing and comprehensive alarm & tripping indication systems.',
      image: null
    },
    {
      icon: <Activity className="w-12 h-12" />,
      title: 'Electrical & Instrumentation Supply & Integration',
      description: 'Complete supply and integration of electrical and instrumentation equipment for industrial applications.',
      features: ['Armoured FRLS Cables', 'Non-Armoured Cables', 'Pressure Transmitters', 'Remote Valves', 'Temperature Gauges'],
      image: null
    }
  ];

  const capabilities = [
    'PLC Programming',
    'SCADA Programming',
    'HMI Programming',
    'PLC Panel Manufacturing',
    'IIoT Solution Development',
    'Electrical Supplies (Cables, Connectors)',
    'Supply of PLCs, HMIs, and Drives'
  ];

  return (
    <div>
      <section 
        className="relative py-24 bg-[#1E56A0] text-white overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1581092160562-40aa08e78837?q=80&w=2070&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#1E56A0]/95"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 lg:px-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4" data-testid="services-heading">
            Our Services
          </h1>
          <p className="text-xl text-slate-200 max-w-3xl mx-auto leading-relaxed">
            Comprehensive automation and monitoring solutions tailored for industrial excellence
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white border-2 border-slate-200 rounded-sm overflow-hidden hover:border-[#FF7F2A] hover:shadow-xl transition-all duration-300 group h-full flex flex-col"
                data-testid={`service-card-${index}`}
              >
                {service.image && (
                  <div className="relative overflow-hidden h-48">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                  </div>
                )}
                <div className="p-6 flex-grow flex flex-col">
                  <div className="bg-[#1E56A0] text-white rounded-sm p-3 w-fit mb-4 group-hover:bg-[#FF7F2A] transition-colors duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3 group-hover:text-[#1E56A0] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-base leading-relaxed text-slate-600 mb-4 flex-grow">
                    {service.description}
                  </p>
                  {service.features && (
                    <div className="space-y-2 pt-4 border-t border-slate-200">
                      {service.features.map((feature, fIndex) => (
                        <div key={fIndex} className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 bg-[#FF7F2A] rounded-full mt-2 flex-shrink-0"></div>
                          <span className="text-sm text-slate-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Technical Capabilities
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Expertise across the full spectrum of industrial automation technologies
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((capability, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-sm hover:shadow-lg transition-all duration-300 border-l-4 border-[#FF7F2A] group"
                data-testid={`capability-item-${index}`}
              >
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-[#1E56A0] rounded-full mt-2 flex-shrink-0 group-hover:scale-150 transition-transform"></div>
                  <p className="font-medium text-slate-900 group-hover:text-[#1E56A0] transition-colors">{capability}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
